/**
 * EquipmentsListing component.
 * @module components/View/EquipmentsListing
 */
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { BodyClass, hasBlocksData, flattenToAppURL } from '@plone/volto/helpers';
import { UniversalLink, PreviewImage, Pagination } from '@plone/volto/components';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { Container, Grid, Label, Form, Header, Loader } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useDebounce } from "use-debounce";
import { keys, isEmpty, map, filter } from 'lodash';
import { ClusterMap } from '../';


const messages = defineMessages({
  labelText: {
    id: 'By words',
    defaultMessage: 'By words',
  },
  placeholderText: {
    id: 'What are you looking for?',
    defaultMessage: 'What are you looking for?',
  },
  defaultOptionEquipmentTypes: {
    id: 'All equipment types',
    defaultMessage: 'All equipment types',
  },
  labelEquipmentType: {
    id: 'By category',
    defaultMessage: 'By category',
  },
  placeholderAllEquipmentTypes: {
    id: 'All equipmentTypes',
    defaultMessage: 'All equipmentTypes',
  },
  categoriesList: {
    id: 'Tipus',
    defaultMessage: 'Tipus'
  },
});


const EquipmentsListing = (props) => {
  const { content } = props;
  const lang = content.language.token;
  const dispatch = useDispatch();
  const intl = useIntl();
  const equipments = useSelector((state) => state.search.subrequests.equipments?.items);
  const total = useSelector((state) => state.search.subrequests.equipments?.total);
  const isLoading = useSelector((state) => state.search.subrequests.equipments?.loading);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [search, setSearch] = useState(0);
  const [searchableTextInput, setSearchableText] = useState('');
  const [searchableText] = useDebounce(searchableTextInput, 500);
  const [equipmentType, setEquipmentType] = useState('');
  const customRoot = useSelector((state) => state.navroot.data?.custom_root);

  const vocabularyEquipmentTypes = 'product.palafolls.vocabularies.EquipmentTypes';
  const equipmentTypeData = useSelector(
    (state) => state.vocabularies[vocabularyEquipmentTypes]?.items ?? [],
    shallowEqual,
  );
  const equipmentTypeItems = useMemo(() => [
    { value: '', label: intl.formatMessage(messages.defaultOptionEquipmentTypes) },
    ...equipmentTypeData,
  ], [equipmentTypeData]);

  const getEquipmentType = function (equipmentType) {
    if (!equipmentTypeItems) {
      return equipmentType
    }
    for (let item of equipmentTypeItems) {
      if (item.value === equipmentType) return item.label
    }
    return equipmentType
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    if (categoryParam) {
        setEquipmentType(categoryParam);
    }
    setIsInitialized(true);
}, []);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: vocabularyEquipmentTypes, lang: content.language.token }));
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized) return;
    let path = content['@id']
    let contentFilter = {
      portal_type: ['Equipment'],
      b_size: 9999,
      b_start: 0,
      review_state: 'published',
      SearchableText: searchableText,
      sort_on: 'getObjPositionInParent',
      metadata_fields: ['effective', 'equipment_types', 'hasPreviewImage', 'featured', 'geolocation']
    };

    if (equipmentType && equipmentType !== '') {
      contentFilter.equipment_types = equipmentType;
    }

    if (customRoot?.['@type'] === 'Minisite' && customRoot?.['UID']) {
      path = '/'
      contentFilter['related_minisites'] = customRoot['UID']
    }
  
    dispatch(
      searchContent(
        flattenToAppURL(path),
        contentFilter,
        'equipments'
      )
    );
  }, [dispatch, searchableText, equipmentType, customRoot, search, isInitialized]);

  const pagedEquipments = useMemo(() => {
    if (!equipments) return [];
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return equipments.slice(start, end);
  }, [equipments, currentPage, pageSize]);

  useEffect(() => {
    const searchForm = document.querySelector('.searchList');
    const searchFormPos = searchForm ? searchForm.offsetTop : 0;
    let scrollPos = window.scrollY;
    if (scrollPos > searchFormPos && searchForm) {
      window.scrollTo({ top: searchFormPos })
    }
  }, [currentPage]);

  const handleSubjectClick = (event, subjectValue) => {
    event.preventDefault();
    event.stopPropagation();
    setEquipmentType(subjectValue);
    setCurrentPage(0);
	};

  const onChangePage = (event, activePage) => {
    event.preventDefault();
    setCurrentPage(activePage.value);
  };

  const onChangePageSize = (event) => {
    event.preventDefault();
    setPageSize(parseInt(event.target.text));
    setCurrentPage(0);
  };

  const onChangeSearchableText = (event, data) => {
    event.preventDefault();
    setSearchableText(data.value);
    setCurrentPage(0);
  };

  const onChangeEquipmentType = (event, data) => {
    event.preventDefault();
    setEquipmentType(data.value);
    setCurrentPage(0);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSearch(search + 1);
    setCurrentPage(0);
  };

  return (
    <>
      <BodyClass className='equipment-list-view' />


      {hasBlocksData(content) ? (
        <>
          <RenderBlocks {...props} />
        </>
      ) : null}


      <div className="equipmentsList__map mb-5 pb-3">
        <ClusterMap markers={equipments} />
      </div>

      <Form className="searchList" onSubmit={onSubmit}>
        <Header as="h2" className='justify-content-center searchList__title' size='small' textAlign='center'><FormattedMessage id="Filter" defaultMessage="Filter" /></Header>
        <Form.Group widths="equal" className="searchList__formGroup align-items-end">
          <Form.Input
            id="textInputId"
            fluid
            label={intl.formatMessage(messages.labelText)}
            focus
            name="searchableText"
            value={searchableTextInput}
            onChange={onChangeSearchableText}
            placeholder={intl.formatMessage(messages.placeholderText)}
            type="text"
          />
          <Form.Select
            id="equipmentTypeSelectId"
            fluid
            label={intl.formatMessage(messages.labelEquipmentType)}
            options={map(equipmentTypeItems, (item, index) => {
              return {
                key: index,
                value: item.value,
                text: item.label,
              };
            })}
            name="equipmentType"
            value={equipmentType}
            onChange={onChangeEquipmentType}
            placeholder={intl.formatMessage(messages.placeholderAllEquipmentTypes)}
          />

        </Form.Group>
      </Form>

      {isLoading ? (
        <div className='mt-4'><Loader active inline="centered" size="large" content={intl.formatMessage({ id: 'Loading...', defaultMessage: 'Loading...' })} /></div>
      ) : (
        <>
          {!isEmpty(pagedEquipments) ? (
            <>
              <Grid className='mt-4'>
                <Grid.Row as='ul' columns={12} className='pt-5 list-unstyled my-0'>
                  {pagedEquipments.map((item) => (
                    <Grid.Column as='li' className='mb-4' mobile={12} tablet={6} computer={4} key={item['@id']}>

                      <article className='equipmentsListItem position-relative'>
                        {item.image_field && (
                          <div className='equipmentsListItem__figure'>
                            <PreviewImage
                              className="equipmentsListItem__img"
                              item={item}
                              alt=''
                              sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, 33.33vw"
                            />
                          </div>
                        )}
                        <div className="equipmentsListItem__text">
                          {item?.equipment_types ? (
                            <ul className="equipmentsListItem__extra list-unstyled" aria-label={intl.formatMessage(messages.categoriesList)}>
                              {map(item.equipment_types, (item, index) => (
                                <li key={item}>
                                  <a href={`?category=${encodeURIComponent(item)}`} onClick={(e) => handleSubjectClick(e, item)}
                                    className="equipmentsListItem__subject__item">{getEquipmentType(item)}</a>
                                </li>
                              ))}
                            </ul>
                          ) : null}

                          <h2 className="equipmentsListItem__title">
                            <a href={flattenToAppURL(item['@id'])} className="equipmentsListItem__link stretched-link ">{item.title}</a>
                          </h2>
                          {item.description && (<p className="equipmentsListItem__desc">{item.description}</p>)}
                          <div className="text-end mt-auto">
                            <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </div>
                      </article>
                    </Grid.Column>
                  ))}


                </Grid.Row>
              </Grid>
              {total > pageSize && (
                <nav className="pagination">
                  <Pagination
                    current={currentPage}
                    total={Math.ceil(total / pageSize)}
                    onChangePage={onChangePage}
                    onChangePageSize={onChangePageSize}
                  />
                </nav>
              )}
            </>
          ) : (
            <div className="pt-5 my-0">
              <p><FormattedMessage id="No equipments found" defaultMessage="No equipments found" /></p>
            </div>
          )}
        </>
      )}

    </>
  );
};

export default EquipmentsListing;